import { Link } from "react-router-dom";
import {
  Server, Cpu, Database, Network, HardDrive, Shield, Cloud, Terminal,
  ArrowRight, CheckCircle2, Zap,
} from "lucide-react";

const servers = [
  {
    id: "vps-linux",
    icon: Server,
    eyebrow: "KVM Sanal",
    title: "Linux VPS",
    description: "Ubuntu, Debian, CentOS ve AlmaLinux üzerinde tam root erişimli sanal sunucular — NVMe SSD ile hızlı, esnek ve ölçeklenebilir.",
    image: "https://images.pexels.com/photos/17489152/pexels-photo-17489152.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      { icon: Terminal, t: "Tam root erişimi", d: "SSH + kök dizin kontrolü." },
      { icon: HardDrive, t: "NVMe SSD", d: "Milyon IOPS performans." },
      { icon: Network, t: "1 Gbps ağ", d: "Sınırsız iç trafik." },
      { icon: Zap, t: "60 sn kurulum", d: "Anlık dağıtım." },
    ],
  },
  {
    id: "vps-windows",
    icon: Server,
    eyebrow: "KVM Sanal",
    title: "Windows VPS",
    description: "Windows Server 2019/2022 lisanslı, RDP erişimli VPS sunucular. .NET ve MS SQL uyumlu, kurumsal iş yükleri için optimize.",
    image: "https://images.unsplash.com/photo-1770885653473-ca48b4d69173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzcwNjUwMDh8MA&ixlib=rb-4.1.0&q=85",
    features: [
      { icon: Terminal, t: "RDP erişimi", d: "Uzak masaüstü kontrolü." },
      { icon: Shield, t: "Lisans dahil", d: "Windows Server ücretsiz." },
      { icon: Cpu, t: "Dedicated CPU", d: "Çekirdekler paylaşılmaz." },
      { icon: Database, t: "MS SQL uyumlu", d: "Enterprise veritabanı." },
    ],
  },
  {
    id: "dedicated",
    icon: HardDrive,
    eyebrow: "Fiziksel",
    title: "Dedicated Sunucular",
    description: "AMD EPYC ve Intel Xeon işlemcilerle size özel fiziksel sunucular — yüksek RAM, yüksek disk ve sınırsız yapılandırma.",
    image: "https://images.unsplash.com/photo-1606778303077-3780ea8d5420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwY2FibGUlMjBibHVlfGVufDB8fHx8MTc3NzA2NTAwOHww&ixlib=rb-4.1.0&q=85",
    features: [
      { icon: Cpu, t: "Özel donanım", d: "32 çekirdeğe kadar." },
      { icon: Database, t: "2 TB NVMe", d: "Full RAID-10." },
      { icon: Network, t: "10 Gbps port", d: "Kurumsal network." },
      { icon: Shield, t: "IPMI / KVM", d: "Uzaktan yönetim." },
    ],
  },
  {
    id: "cloud",
    icon: Cloud,
    eyebrow: "Esnek",
    title: "Cloud Sunucular",
    description: "Yüklere göre dinamik ölçeklenen, saatlik faturalanan bulut sunucular. Kubernetes ve Terraform uyumlu.",
    image: "https://images.pexels.com/photos/36750789/pexels-photo-36750789.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      { icon: Zap, t: "Saniye ölçekleme", d: "Anlık yatay/dikey." },
      { icon: Database, t: "Snapshot & klon", d: "Dakikada geri yükle." },
      { icon: Network, t: "Private VPC", d: "Yalıtılmış iç ağ." },
      { icon: Terminal, t: "API & CLI", d: "Full otomasyon." },
    ],
  },
];

export default function Servers() {
  return (
    <div data-testid="servers-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 lg:pt-24 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">Sunucular</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Güçlü sunucular, <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">ihtiyacınıza göre.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto">
            VPS, dedicated veya cloud — dünya genelinde 42 veri merkezinde tam root erişimi ve
            yüksek performans.
          </p>
        </div>
      </section>

      <section className="relative pb-20 space-y-20">
        {servers.map((s, idx) => {
          const reversed = idx % 2 === 1;
          return (
            <div key={s.id} id={s.id} data-testid={`server-detail-${s.id}`} className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-12 items-center`}>
                <div className={reversed ? "lg:order-2" : ""}>
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-3 py-1 mb-6">
                    <s.icon size={14} className="text-blue-400" />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">{s.eyebrow}</span>
                  </div>
                  <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-6">{s.title}</h2>
                  <p className="text-lg text-zinc-400 leading-relaxed mb-8">{s.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {s.features.map((f) => (
                      <div key={f.t} className="glass rounded-2xl p-5">
                        <f.icon size={18} className="text-blue-400 mb-3" strokeWidth={1.6} />
                        <div className="font-semibold text-sm mb-1">{f.t}</div>
                        <div className="text-xs text-zinc-500 leading-relaxed">{f.d}</div>
                      </div>
                    ))}
                  </div>

                  <Link to="/fiyatlandirma" data-testid={`server-cta-${s.id}`} className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_0_30px_rgba(0,122,255,0.3)] transition-all">
                    Paketleri gör <ArrowRight size={15} />
                  </Link>
                </div>

                <div className={`relative ${reversed ? "lg:order-1" : ""}`}>
                  <div className="absolute -inset-8 bg-blue-600/10 blur-3xl rounded-full pointer-events-none" />
                  <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                    <img src={s.image} alt={s.title} className="w-full h-[460px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                          <CheckCircle2 size={16} className="text-blue-400" />
                        </div>
                        <div>
                          <div className="text-xs text-zinc-400">Kurulum süresi</div>
                          <div className="font-heading font-semibold">60 saniyenin altında</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="relative py-20 border-t border-white/5 bg-[#07080C]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Avantajlar</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Dakikalar içinde canlı.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Zap, t: "Hızlı dağıtım", d: "60 sn'de hazır sunucu." },
              { i: Shield, t: "DDoS korumalı", d: "Tüm sunucularda dahil." },
              { i: Network, t: "Global ağ", d: "6 kıtada 42 lokasyon." },
              { i: Terminal, t: "Tam kontrol", d: "Root / RDP erişimi." },
            ].map((item, i) => (
              <div key={i} data-testid={`server-advantage-${i}`} className="card-hover glass rounded-2xl p-7">
                <item.i size={22} strokeWidth={1.6} className="text-blue-400 mb-5" />
                <h3 className="font-heading text-lg font-semibold mb-2">{item.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
